import { useEffect, useId, type ReactNode } from "react";
import { cx } from "@its-engineering-and-development/ui-foundation/utils";
import { LoadingState, LOADING_STATE_VARIANT } from "@app/components/base/loading-state/loading-state";

function IconX() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
    );
}

export interface DrawerProps {
    /** Title shown in the drawer header. */
    title: string;
    /** Optional supporting text shown below the title. */
    description?: string;
    /** Called when the drawer requests to close (backdrop, escape, or close button). */
    onClose: () => void;
    /** When true, prevents closing via backdrop, escape, or the close button. */
    isBusy?: boolean;
    /**
     * Max width of the panel (any CSS length).
     * @default "560px"
     */
    maxWidth?: string;
    /** When true, shows a loading overlay over the body. */
    isLoading?: boolean;
    /** Accessible label for the loading overlay. Defaults to the drawer title. */
    loadingLabel?: string;
    /** Optional message rendered under the loading spinner. */
    loadingMessage?: string;
    /** Classes applied to the inner body wrapper, overriding the default padding/gap. */
    bodyClassName?: string;
    /** Content rendered in the sticky footer, typically action buttons. */
    footer?: ReactNode;
    /** Main body content. */
    children: ReactNode;
}

/**
 * Generic right-side drawer panel. Renders a backdrop, header with a close button,
 * a scrollable body for arbitrary content, an optional loading overlay, and an
 * optional sticky footer.
 */
export function Drawer({
    title,
    description,
    onClose,
    isBusy = false,
    maxWidth = "560px",
    isLoading = false,
    loadingLabel,
    loadingMessage,
    bodyClassName,
    footer,
    children,
}: DrawerProps) {
    const titleId = useId();

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !isBusy) onClose();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [onClose, isBusy]);

    return (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 z-40 bg-black/20" onClick={() => !isBusy && onClose()} aria-hidden="true" />

            {/* Panel */}
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                style={{ maxWidth }}
                className="fixed inset-y-0 right-0 z-50 flex w-full flex-col overflow-hidden bg-white shadow-2xl"
            >
                {/* Header */}
                <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
                    <div>
                        <h2 id={titleId} className="text-xl font-semibold text-gray-900">
                            {title}
                        </h2>
                        {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
                    </div>
                    <button
                        type="button"
                        aria-label="Close"
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                        onClick={onClose}
                        disabled={isBusy}
                    >
                        <IconX />
                    </button>
                </div>

                {/* Scrollable body */}
                <div className="relative flex-1 overflow-y-auto">
                    {isLoading && <LoadingState variant={LOADING_STATE_VARIANT.Overlay} label={loadingLabel ?? title} message={loadingMessage} />}
                    <div className={cx("flex flex-col gap-6 px-5 py-6", bodyClassName)}>{children}</div>
                </div>

                {/* Footer */}
                {footer && <div className="border-t border-gray-200 px-5 py-4">{footer}</div>}
            </div>
        </>
    );
}
